import React from 'react';
import PropTypes from 'prop-types';
import CheckboxGroup from './CheckboxGroup';

// Inline editor for one custom generation rule: the condition type, the
// membership-signup window, and the three optional filter groups (prepay /
// plan / account status — ANDed across groups, ORed within a group).
function CustomRuleEditor({ rule, meta, saving, onChange }) {
  const filters = rule.filters || {};
  const set_filter = (key, value) => onChange({ ...rule, filters: { ...filters, [key]: value } });

  return (
    <div className="mt-3 rounded-md border border-line bg-surface-card px-3 py-2 flex flex-col gap-2">
      <div>
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
          Condition
        </div>
        <select
          value={rule.condition}
          onChange={(e) => onChange({ ...rule, condition: e.target.value })}
          disabled={saving}
          className="w-full rounded-md border border-line bg-surface-card px-2 py-1.5 text-sm text-content-primary"
          style={{ maxWidth: 420 }}
        >
          {(meta.condition_options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
          Membership signup
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(meta.signup_options || []).map((o) => {
            const is_sel = (filters.signup_window || 'all_time') === o.value;
            return (
              <div
                key={o.value}
                role="button"
                tabIndex={0}
                onClick={() => !saving && set_filter('signup_window', o.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !saving) {
                    e.preventDefault();
                    set_filter('signup_window', o.value);
                  }
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer select-none border ${
                  is_sel
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-surface-card text-content-secondary border-line hover:border-line-emphasis'
                }`}
              >
                {o.label}
              </div>
            );
          })}
        </div>
      </div>
      <CheckboxGroup
        label="Prepay type"
        options={meta.prepay_options || []}
        selected={filters.prepay_type || []}
        onChange={(v) => set_filter('prepay_type', v)}
        disabled={saving}
      />
      <CheckboxGroup
        label="Plan type"
        options={meta.plan_options || []}
        selected={filters.plan_type || []}
        onChange={(v) => set_filter('plan_type', v)}
        disabled={saving}
      />
      <CheckboxGroup
        label="Account status"
        options={meta.status_options || []}
        selected={filters.account_status || []}
        onChange={(v) => set_filter('account_status', v)}
        disabled={saving}
      />
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Filters are combined with AND; values within a group with OR. Generated checklists are named
        after this rule&apos;s summary.
      </div>
    </div>
  );
}

CustomRuleEditor.propTypes = {
  rule: PropTypes.shape({
    condition: PropTypes.string,
    filters: PropTypes.shape({
      signup_window: PropTypes.string,
      prepay_type: PropTypes.arrayOf(PropTypes.string),
      plan_type: PropTypes.arrayOf(PropTypes.string),
      account_status: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  meta: PropTypes.shape({
    condition_options: PropTypes.arrayOf(PropTypes.shape({})),
    signup_options: PropTypes.arrayOf(PropTypes.shape({})),
    prepay_options: PropTypes.arrayOf(PropTypes.shape({})),
    plan_options: PropTypes.arrayOf(PropTypes.shape({})),
    status_options: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  saving: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

export default CustomRuleEditor;
